/**
 * Utility for dynamically loading Google Fonts
 */

const loadedFonts = new Set();

/**
 * Load a Google Font dynamically
 * @param {string} fontFamily - Font family name (e.g., "Poppins", "Inter")
 * @returns {Promise<void>}
 */
export function loadGoogleFont(fontFamily) {
  // Normalize font name (remove spaces, handle variants)
  const normalizedName = fontFamily.split(',')[0].trim().replace(/\s+/g, '+');
  
  // Skip if already loaded
  if (loadedFonts.has(normalizedName)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    // Check if link already exists
    const existingLink = document.querySelector(`link[href*="${normalizedName}"]`);
    if (existingLink) {
      loadedFonts.add(normalizedName);
      resolve();
      return;
    }

    // Create link element
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${normalizedName}:wght@300;400;500;600;700;800&display=swap`;
    link.onload = () => {
      loadedFonts.add(normalizedName);
      resolve();
    };
    link.onerror = () => {
      console.warn(`Failed to load font: ${fontFamily}`);
      resolve(); // Resolve anyway to not block rendering
    };
    document.head.appendChild(link);
  });
}

/**
 * Load multiple fonts from an array
 * @param {string[]} fonts - Array of font family names
 * @returns {Promise<void>}
 */
export function loadFonts(fonts) {
  if (!fonts || !Array.isArray(fonts) || fonts.length === 0) {
    return Promise.resolve();
  }

  // Extract primary font names (before comma in font-family strings)
  const fontNames = fonts
    .map(font => {
      // Handle both "Poppins" and "Poppins, Arial, sans-serif" formats
      const firstFont = font.split(',')[0].trim();
      // Only load Google Fonts (skip system fonts)
      const googleFonts = ['Poppins', 'Inter', 'Roboto', 'Open Sans', 'Montserrat', 'Lato', 'Raleway', 'Oswald', 'Playfair Display', 'Merriweather'];
      return googleFonts.includes(firstFont) ? firstFont : null;
    })
    .filter(Boolean);

  // Load all fonts
  return Promise.all(fontNames.map(loadGoogleFont)).then(() => {});
}

/**
 * Extract font families from data object recursively
 * @param {any} data - Data object to search
 * @param {Set<string>} foundFonts - Set to store found fonts
 */
function extractFontsFromData(data, foundFonts = new Set()) {
  if (!data || typeof data !== 'object') return;

  if (Array.isArray(data)) {
    data.forEach(item => extractFontsFromData(item, foundFonts));
    return;
  }

  // Check for common font-related keys
  if (data.fonts && Array.isArray(data.fonts)) {
    data.fonts.forEach(font => {
      if (typeof font === 'string') {
        // Extract first font from font-family strings like "Poppins, Arial, sans-serif"
        const firstFont = font.split(',')[0].trim();
        if (firstFont) foundFonts.add(firstFont);
      }
    });
  }

  if (data.fontFamily && typeof data.fontFamily === 'string') {
    // Extract first font from font-family strings
    const firstFont = data.fontFamily.split(',')[0].trim();
    if (firstFont) foundFonts.add(firstFont);
  }

  if (data.theme && data.theme.fontFamily) {
    if (Array.isArray(data.theme.fontFamily)) {
      data.theme.fontFamily.forEach(font => {
        if (typeof font === 'string') {
          const firstFont = font.split(',')[0].trim();
          if (firstFont) foundFonts.add(firstFont);
        }
      });
    } else if (typeof data.theme.fontFamily === 'string') {
      const firstFont = data.theme.fontFamily.split(',')[0].trim();
      if (firstFont) foundFonts.add(firstFont);
    }
  }

  // Recursively search all properties
  Object.values(data).forEach(value => {
    if (typeof value === 'object' && value !== null) {
      extractFontsFromData(value, foundFonts);
    }
  });
}

/**
 * Load fonts from data object
 * @param {any} data - Data object containing font information
 * @returns {Promise<void>}
 */
export function loadFontsFromData(data) {
  const foundFonts = new Set();
  extractFontsFromData(data, foundFonts);
  
  if (foundFonts.size === 0) {
    return Promise.resolve();
  }

  return loadFonts(Array.from(foundFonts));
}

/**
 * Get font family string from data with fallbacks
 * @param {any} data - Data object
 * @returns {string} Font family string
 */
export function getFontFamily(data) {
  let fonts = [];

  // Try to extract from data.theme.fontFamily
  if (data?.theme?.fontFamily) {
    if (Array.isArray(data.theme.fontFamily)) {
      fonts = [...data.theme.fontFamily];
    } else if (typeof data.theme.fontFamily === 'string') {
      fonts = [data.theme.fontFamily];
    }
  }

  // Try to extract from data.fonts
  if (data?.fonts && Array.isArray(data.fonts)) {
    fonts = [...data.fonts];
  }

  // Default fallbacks
  if (fonts.length === 0) {
    fonts = ['Inter', 'system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Arial', 'sans-serif'];
  } else {
    // Add system font fallbacks
    fonts.push('system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Arial', 'sans-serif');
  }

  return fonts.join(', ');
}

